<!--
  - Copyright (c) 2022-2023. Haodong JU
  -->

<template>
  <div>
    <v-card-text class="pa-0">
      <v-row dense>
        <v-col
          v-if="showTag"
          cols="12"
        >
          <v-chip-group
            v-model="selectedPreset"
            active-class="primary white--text"
            class="mb-2"
            column
          >
            <v-chip
              v-for="d in currentTimeList"
              :key="d.label"
              :value="d.label"
              small
              @click="dateRange = d.dateRange()"
            >
              {{ $t(d.label) }}
            </v-chip>
          </v-chip-group>
        </v-col>
        <v-col cols="6">
          <v-menu
            v-model="startDateMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            min-width="auto"
            offset-y
            transition="scale-transition"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                v-model="startDate"
                :label="$t('from')"
                dense
                hide-details
                outlined
                prepend-inner-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="startDate"
              v-bind="needMax ? { max: endDate || today } : {}"
              @input="startDateMenu = false"
            />
          </v-menu>
        </v-col>
        <v-col cols="6">
          <v-menu
            v-model="endDateMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            min-width="auto"
            offset-y
            transition="scale-transition"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                v-model="endDate"
                :label="$t('to')"
                dense
                hide-details
                outlined
                prepend-inner-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="endDate"
              v-bind="needMax ? { max: today, min: startDate } : {}"
              @input="endDateMenu = false"
            />
          </v-menu>
        </v-col>
      </v-row>
    </v-card-text>
  </div>
</template>

<script>
import { predefinedDateRangeList, today } from '@/model/utils/DateRepository'

export default {
  name: 'NormalDatePicker',
  props: {
    value: {
      type: Array
    },
    showTag: {
      type: Boolean,
      default: true
    },
    needMax: {
      type: Boolean,
      default: true
    }
  },
  data: function () {
    return {
      today,
      startDate: null,
      endDate: null,
      menu: false,
      startDateMenu: false,
      endDateMenu: false,
      selectedPreset: null,
      predefinedTimeList: predefinedDateRangeList
    }
  },
  computed: {
    currentTimeList () {
      const dayOfWeek = new Date().getDay()
      const dayOfMonth = new Date().getDate()
      const month = new Date().getMonth()
      let list = this.predefinedTimeList
      if (dayOfWeek === 1) {
        list = this.predefinedTimeList.filter(it => it.label !== 'thisWeek')
      }
      if (dayOfMonth === 1) {
        if (month === 0) {
          list = this.predefinedTimeList.filter(it => it.label !== 'thisYear')
        }
        list = this.predefinedTimeList.filter(it => it.label !== 'thisMonth')
      }
      return list
    },
    dateRange: {
      get () {
        return [this.startDate ?? this.endDate ?? today, this.endDate ?? this.startDate ?? today]
      },
      set (val) {
        if (val?.length === 2) {
          [this.startDate, this.endDate] = val
          // Find and set the selected preset if it matches
          this.updateSelectedPreset()
        }
      }
    },
    dateRangeText () {
      if (!this.startDate && !this.endDate) return ''

      // If a preset is selected, show its name
      if (this.selectedPreset) {
        return this.$t(this.selectedPreset)
      }

      // Otherwise show the date range
      return this.startDate === this.endDate
        ? this.startDate
        : `${this.startDate} ~ ${this.endDate}`
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (val) {
        this.dateRange = val
      }
    },
    startDate (val) {
      if (val && this.endDate && val > this.endDate) {
        this.endDate = val
      }
      this.updateSelectedPreset()
    },
    endDate (val) {
      if (val && this.startDate && val < this.startDate) {
        this.startDate = val
      }
      this.updateSelectedPreset()
    },
    dateRange (val) {
      this.$emit('input', val)
    }
  },
  methods: {
    updateSelectedPreset () {
      // Find if current date range matches any preset
      const currentRange = [this.startDate, this.endDate].join()
      const matchingPreset = this.currentTimeList.find(d => d.dateRange().join() === currentRange)
      this.selectedPreset = matchingPreset ? matchingPreset.label : null
    }
  }
}
</script>

<style scoped>
.v-chip-group {
  flex-wrap: wrap;
}
</style>
